import React, { useEffect, useState } from "react";
import { Trash2, Plus } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { money, num, Spinner, PageHeader, Empty } from "@/components/shared";
import { useAuth } from "@/context/AuthContext";
import { FieldW, SelectBox } from "@/pages/Clients";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

const REASONS = ["Expired", "Kitchen Damage", "Preparation Waste", "Spoiled", "Other"];
const RESP = ["Client", "Dish Factory", "Supplier", "Under Review"];

const RESP_NOTE = {
  Client: "A client-facing charge will be created.",
  "Dish Factory": "Stock reduced. Client is NOT charged.",
  Supplier: "Stock reduced/held and flagged. Client is NOT charged.",
  "Under Review": "Stock reduced. Responsibility pending — no charge yet.",
};

export default function Wastage() {
  const { user } = useAuth();
  const canAdd = ["admin", "kitchen"].includes(user.role);
  const [list, setList] = useState(null);
  const [ings, setIngs] = useState([]);
  const [brands, setBrands] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.get("/wastage").then((r) => setList(r.data));
    api.get("/ingredients").then((r) => setIngs(r.data));
    api.get("/brands").then((r) => setBrands(r.data));
  };
  useEffect(() => { load(); }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const openNew = () => { setForm({ ingredient_id: ings[0]?.id, quantity: 0, reason: "Expired", responsibility: "Dish Factory", brand_id: "", notes: "" }); setOpen(true); };

  const save = async () => {
    setBusy(true);
    try {
      await api.post("/wastage", { ...form, quantity: Number(form.quantity), brand_id: form.brand_id || null });
      toast.success("Wastage recorded");
      setOpen(false); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (!list) return <Spinner />;

  return (
    <div>
      <PageHeader title="Wastage / Expiry / Damage" subtitle="Log spoilage and assign financial responsibility">
        {canAdd && (
          <Button onClick={openNew} data-testid="add-wastage-btn" className="gap-1.5 bg-primary text-primary-foreground hover:bg-amber-400 font-semibold">
            <Plus className="h-4 w-4" /> Record Wastage
          </Button>
        )}
      </PageHeader>

      {list.length === 0 ? <Empty icon={Trash2} title="No wastage recorded" /> : (
        <div className="card-surface overflow-x-auto">
          <Table>
            <TableHeader><TableRow className="hover:bg-transparent border-border">
              <TableHead>Date</TableHead><TableHead>Ingredient</TableHead><TableHead className="text-right">Qty</TableHead>
              <TableHead>Reason</TableHead><TableHead>Responsibility</TableHead>
              <TableHead className="text-right">Value</TableHead><TableHead className="text-right">Charged</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {list.map((w) => (
                <TableRow key={w.id} className="border-border" data-testid={`wastage-row-${w.id}`}>
                  <TableCell className="font-mono text-xs text-muted-foreground">{w.date}</TableCell>
                  <TableCell className="text-white font-medium">{w.ingredient_name}</TableCell>
                  <TableCell className="text-right font-mono text-slate-200">{num(w.quantity)} {w.unit}</TableCell>
                  <TableCell className="text-slate-300">{w.reason}</TableCell>
                  <TableCell><span className={w.responsibility === "Client" ? "text-amber-300" : "text-slate-300"}>{w.responsibility}</span></TableCell>
                  <TableCell className="text-right font-mono text-slate-300">{money(user.role === "client" ? w.client_value : w.internal_value)}</TableCell>
                  <TableCell className="text-right">{w.charged ? <span className="text-amber-300 font-mono text-sm">{money(w.client_value)}</span> : <span className="text-muted-foreground text-sm">—</span>}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md bg-card border-border">
          <DialogHeader><DialogTitle>Record Wastage</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <FieldW label="Ingredient"><SelectBox value={form.ingredient_id} onChange={(v) => set("ingredient_id", v)} options={ings.map((i) => ({ value: i.id, label: `${i.name} (${i.purchase_unit})` }))} testid="wastage-ingredient" /></FieldW>
            <FieldW label="Quantity (purchase units)"><Input type="number" data-testid="wastage-qty" value={form.quantity} onChange={(e) => set("quantity", e.target.value)} /></FieldW>
            <FieldW label="Reason"><SelectBox value={form.reason} onChange={(v) => set("reason", v)} options={REASONS} testid="wastage-reason" /></FieldW>
            <FieldW label="Responsibility"><SelectBox value={form.responsibility} onChange={(v) => set("responsibility", v)} options={RESP} testid="wastage-responsibility" /></FieldW>
            <FieldW label="Brand (optional)"><SelectBox value={form.brand_id || "none"} onChange={(v) => set("brand_id", v === "none" ? "" : v)} options={[{ value: "none", label: "— None —" }, ...brands.map((b) => ({ value: b.id, label: b.name }))]} /></FieldW>
            <FieldW label="Notes"><Textarea rows={2} value={form.notes} onChange={(e) => set("notes", e.target.value)} /></FieldW>
            <p className="text-xs text-amber-400/90 bg-amber-500/10 border border-amber-500/20 rounded-lg p-2.5" data-testid="wastage-resp-note">{RESP_NOTE[form.responsibility]}</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={busy} data-testid="save-wastage-btn" className="bg-primary text-primary-foreground hover:bg-amber-400 font-semibold">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
